import { ChevronDown, ChevronLeft } from "@untitledui/icons";
import { Button as AriaButton } from "react-aria-components";

import { Dropdown } from "@/components/ui/dropdown/dropdown-index";
import { cx } from "@/utils/cx";

import type { MapDrillBreadcrumb } from "../../../utils/location/location-map-utils";

import { splitMapDrillBreadcrumbTrail } from "../../../utils/shared/map-drill-breadcrumb-trail";

interface MapDrillBreadcrumbProps {
	crumbs: MapDrillBreadcrumb[];
	onCrumbClick: (crumb: MapDrillBreadcrumb) => void;
}

const CrumbSeparator = () => (
	<span aria-hidden className="shrink-0 text-xs text-quaternary">
		/
	</span>
);

const FloorCrumbMenu = ({
	crumb,
	isLast,
	onCrumbClick,
}: {
	crumb: MapDrillBreadcrumb;
	isLast: boolean;
	onCrumbClick: (crumb: MapDrillBreadcrumb) => void;
}) => (
	<Dropdown.Root>
		<AriaButton
			aria-label={
				crumb.floorId
					? `Select floor, currently ${crumb.label}`
					: "Select floor"
			}
			className={cx(
				"flex max-w-28 min-w-0 items-center gap-0.5 rounded-md text-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
				isLast
					? "font-semibold text-primary"
					: "font-medium text-brand-600 hover:text-brand-700",
			)}
		>
			<span className="truncate">{crumb.label}</span>
			<ChevronDown aria-hidden className="size-3 shrink-0" />
		</AriaButton>
		<Dropdown.Popover className="min-w-44" placement="bottom start">
			<Dropdown.Menu
				aria-label="Floors"
				onAction={(key) => {
					const floorId = String(key);
					if (isLast && floorId === crumb.floorId) return;
					const option = crumb.floorOptions?.find(
						(entry) => entry.id === floorId,
					);
					onCrumbClick({
						floorId,
						floorName: option?.label,
						id: `floor:${floorId}`,
						label: option?.label ?? crumb.label,
						level: "floors",
						locationId: crumb.locationId,
						locationName: crumb.locationName,
					});
				}}
				selectionMode="none"
			>
				{crumb.floorOptions?.map((option) => (
					<Dropdown.Item
						id={option.id}
						isDisabled={isLast && option.id === crumb.floorId}
						key={option.id}
						label={option.label}
					/>
				))}
			</Dropdown.Menu>
		</Dropdown.Popover>
	</Dropdown.Root>
);

/**
 * Single-line trail of where the operator is on the map.
 * Hidden on the all-locations view. More than two parents collapse behind ….
 */
export const MapDrillBreadcrumbNav = ({
	crumbs,
	onCrumbClick,
}: MapDrillBreadcrumbProps) => {
	if (crumbs.length === 0) return null;

	const { collapsed, current, parent, visible } =
		splitMapDrillBreadcrumbTrail(crumbs);

	return (
		<nav
			aria-label="Map drill path"
			className="absolute top-3 left-3 z-20 flex max-w-[calc(100%-4.5rem)] flex-nowrap items-center gap-1 overflow-hidden rounded-lg border border-secondary bg-primary/95 px-1.5 py-1 shadow-sm backdrop-blur-sm"
		>
			{parent ? (
				<button
					aria-label="Back one level"
					className="flex size-6 shrink-0 items-center justify-center rounded-md text-fg-quaternary outline-focus-ring hover:text-fg-secondary focus-visible:outline-2 focus-visible:outline-offset-2"
					onClick={() => onCrumbClick(parent)}
					type="button"
				>
					<ChevronLeft className="size-4" />
				</button>
			) : null}

			{collapsed.length > 0 ? (
				<>
					<Dropdown.Root>
						<AriaButton
							aria-label="Show full map path"
							className="flex size-6 shrink-0 items-center justify-center rounded-md text-xs font-medium text-brand-600 outline-focus-ring hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2"
						>
							…
						</AriaButton>
						<Dropdown.Popover className="min-w-44" placement="bottom start">
							<Dropdown.Menu
								aria-label="Map path"
								onAction={(key) => {
									const crumb = crumbs.find(
										(entry) => entry.id === String(key),
									);
									if (crumb && crumb.id !== current?.id) {
										onCrumbClick(crumb);
									}
								}}
								selectionMode="none"
							>
								{crumbs.map((crumb) => (
									<Dropdown.Item
										id={crumb.id}
										isDisabled={crumb.id === current?.id}
										key={crumb.id}
										label={crumb.label}
									/>
								))}
							</Dropdown.Menu>
						</Dropdown.Popover>
					</Dropdown.Root>
					<CrumbSeparator />
				</>
			) : null}

			{visible.map((crumb, index) => {
				const isLast = index === visible.length - 1;
				const floorOptions = crumb.floorOptions ?? [];
				const isLocationFloorPicker = floorOptions.length > 0 && !crumb.floorId;
				return (
					<span className="flex min-w-0 items-center gap-1" key={crumb.id}>
						{index > 0 ? <CrumbSeparator /> : null}
						{isLocationFloorPicker ? (
							isLast ? (
								<span className="min-w-0 truncate text-xs font-semibold text-primary">
									{crumb.label}
								</span>
							) : (
								<button
									className="max-w-28 shrink truncate text-xs font-medium text-brand-600 underline-offset-2 hover:text-brand-700 hover:underline"
									onClick={() => onCrumbClick(crumb)}
									type="button"
								>
									{crumb.label}
								</button>
							)
						) : null}
						{isLocationFloorPicker ? <CrumbSeparator /> : null}
						{floorOptions.length > 0 ? (
							<FloorCrumbMenu
								crumb={
									isLocationFloorPicker ? { ...crumb, label: "Floor" } : crumb
								}
								isLast={isLast || isLocationFloorPicker}
								onCrumbClick={onCrumbClick}
							/>
						) : isLast ? (
							<span className="min-w-0 truncate text-xs font-semibold text-primary">
								{crumb.label}
							</span>
						) : (
							<button
								className={cx(
									"max-w-28 shrink truncate text-xs font-medium text-brand-600 underline-offset-2 hover:text-brand-700 hover:underline",
								)}
								onClick={() => onCrumbClick(crumb)}
								type="button"
							>
								{crumb.label}
							</button>
						)}
					</span>
				);
			})}
		</nav>
	);
};
